/**
 * Copyright © Ircam Amplify 2024-2026
 */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { combineReducers, configureStore } from '@reduxjs/toolkit'
import {
  useDispatch as useDispatchBase,
  useSelector as useSelectorBase,
} from 'react-redux'
import {
  FLUSH,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
  REHYDRATE,
  createMigrate,
  persistReducer,
  persistStore,
} from 'redux-persist'
import createWebStorage from 'redux-persist/lib/storage/createWebStorage'
import layoutSlice from './slices/layout.slice'
import userSlice, {
  initialState as userInitialState,
} from './slices/user.slice'

const createNoopStorage = () => {
  return {
    getItem(_key: any) {
      return Promise.resolve(null)
    },
    setItem(_key: any, value: any) {
      return Promise.resolve(value)
    },
    removeItem(_key: any) {
      return Promise.resolve()
    },
  }
}

const storage =
  typeof window !== 'undefined'
    ? createWebStorage('local')
    : createNoopStorage()

/**
 * Management of the different versions of UserState of data stored in the Redux
 * local storage.
 */
const migrations: any = {
  // Version 1: fields wrapped in TimeStampValue
  1: () => ({ user: userInitialState }),
  // Version 2: TimeStampValue.timestamp changed from Date to number
  2: () => ({ user: userInitialState }),
  // Version 3: added aimdTaskListSortDirection
  3: () => ({ user: userInitialState }),
  //: Version 4: add priceList
  4: () => ({ user: userInitialState }),
  // Version 5: removed TimeStampValue wrapping
  5: () => ({ user: userInitialState }),
}

/**
 * The latest version of persistent storage.
 */
const PERSIST_VERSION = Math.max(...Object.keys(migrations).map(Number))

const persistConfig = {
  key: 'root',
  version: PERSIST_VERSION,
  storage,
  whitelist: ['user'],
  migrate: createMigrate(migrations, { debug: false }),
}

const reducers = combineReducers({
  user: userSlice,
  layout: layoutSlice,
})

const persistedReducer = persistReducer(persistConfig, reducers)

export const store = configureStore({
  reducer: persistedReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }),
})

export const persistor = persistStore(store)

export type RootState = ReturnType<typeof store.getState>
type AppDispatch = typeof store.dispatch

export const useDispatch = () => useDispatchBase<AppDispatch>()
export const useSelector = <TSelected = unknown>(
  selector: (state: RootState) => TSelected
): TSelected => useSelectorBase<RootState, TSelected>(selector)
